<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue'
import Stats from 'stats.js'
import {
  CameraMode,
  ClipOverlayPlugin,
  ClipPlugin,
  ClipTask,
  GaussianSplatPlugin,
  MeasureOverlayPlugin,
  MeasurePlugin,
  presets,
  type ClipService,
  type ControlMode,
  type MeasureService,
  type MeasureType,
  type ViewPreset,
} from '@sharefe/share3d-engine'
import { getDefaultPresetModelUrl, PRESET_MODELS } from '../examples/presetModels'
import { useExampleGraphicsCard } from '../composables/useExampleGraphicsCard'
import { isGpuTimingRequested } from '../utils/graphicsUrlConfig'
import { toGraphicsRuntimeError } from '../utils/graphicsRuntimeError'
import { use3DGS } from '../hooks/use3DGS'
import {
  configureGsplatPlugins,
  getGsplatGraphicsConfig,
  resolveGsplatBackendControlState,
} from '../utils/gsplatBackendControl'
import {
  applySpaceMouseNavigationProfile,
  applySpaceMouseSecondaryControlSelection,
  buildSpaceMouseSecondaryControlGroups,
  createSpaceMouseNavigationState,
  readSpaceMouseNavigationState,
  type SpaceMouseControllerLike,
  type SpaceMouseNavigationProfile,
  type SpaceMouseNavigationState,
} from '../utils/spaceMouseNavigation'
import SceneAssetLoaderCard from './SceneAssetLoaderCard.vue'
import GSplatBackendControlCard from './GSplatBackendControlCard.vue'
import GSplatScalarFilterCard from './GSplatScalarFilterCard.vue'
import GraphicsBackendControlCard from './GraphicsBackendControlCard.vue'
import SceneClipCard from './SceneClipCard.vue'
import SceneControlPanel from './SceneControlPanel.vue'
import SceneGroupCard from './SceneGroupCard.vue'
import SceneMeasureCard from './SceneMeasureCard.vue'
import SceneStatusCard from './SceneStatusCard.vue'
import SceneViewCard from './SceneViewCard.vue'

type ClipSelectionMode = 'polygon' | 'rectangle' | 'box3d'
type ClipApplyMode = 'inside' | 'outside'

const MEASURE_OPTIONS: Array<{
  value: MeasureType
  label: string
  detail: string
}> = [
    { value: 'coordinate', label: '坐标', detail: '单点坐标，1 点自动完成' },
    { value: 'distance', label: '距离', detail: '实时预览，右键结束' },
    { value: 'height', label: '高度', detail: '2 点自动完成' },
    { value: 'circle', label: '圆', detail: '3 点拟合圆' },
    { value: 'azimuth', label: '方位角', detail: '2 点自动完成' },
    { value: 'angle', label: '角度', detail: '建议 3 点成角，右键结束' },
    { value: 'area', label: '面积', detail: '3 点以上闭合，右键结束' },
  ]

const CLIP_SELECTION_OPTIONS: Array<{
  value: ClipSelectionMode
  label: string
  detail: string
}> = [
    { value: 'polygon', label: '多边形', detail: '逐点圈选后确认裁切' },
    { value: 'rectangle', label: '矩形', detail: '两点框选后确认裁切' },
    { value: 'box3d', label: '3D Box', detail: '默认包围盒，可拖动操控' },
  ]

const CLIP_APPLY_OPTIONS: Array<{
  value: ClipApplyMode
  label: string
  detail: string
}> = [
    { value: 'inside', label: '内裁', detail: '保留选中的范围' },
    { value: 'outside', label: '外裁', detail: '保留未选中的范围' },
  ]

const VIEW_OPTIONS: Array<{
  value: ViewPreset
  label: string
}> = [
    { value: 'top', label: '俯视' },
    { value: 'bottom', label: '仰视' },
    { value: 'front', label: '前视' },
    { value: 'back', label: '后视' },
    { value: 'left', label: '左视' },
    { value: 'right', label: '右视' },
  ]

const CONTROL_MODE_OPTIONS: Array<{
  value: ControlMode
  label: string
  detail: string
}> = [
    { value: 'orbit', label: '轨道', detail: '围绕模型目标点自由观察' },
    { value: 'spacemouse', label: 'SpaceMouse', detail: '对象中心旋转，同时兼容普通鼠标与 3D 鼠标' },
    { value: 'earth', label: '地球', detail: '左键命中模型点后围绕该点旋转' },
    { value: 'map', label: '地图', detail: '更适合平移浏览与地图式交互' },
    { value: 'first-person', label: '第一人称（Three）', detail: '保留 three 官方 FirstPersonControls 手感' },
    { value: 'legacy-first-person', label: '第一人称（旧版）', detail: '左键拖拽旋转、右键平移的旧版漫游' },
    { value: 'fly', label: '飞行（Three）', detail: 'three 官方 FlyControls，自由飞行与拖拽视角' },
  ]

const SPACEMOUSE_DEFAULT_PROFILE: SpaceMouseNavigationProfile = 'mouse-only'

// 默认加载预设 model1
const DEFAULT_GS3D_MODEL = getDefaultPresetModelUrl()
const containerRef = useTemplateRef('containerRef')
const backendControlState = resolveGsplatBackendControlState(window.location.hash, {
  webgpuSupported: true,
})

const graphicsCard = useExampleGraphicsCard({
  presetKey: 'pointCloudAndGSplat',
  extras: ['MeasurePlugin', 'MeasureOverlayPlugin', 'ClipPlugin', 'ClipOverlayPlugin'],
})
const loadSHN = ref(false)
const plugins = [
  ...configureGsplatPlugins(presets.pointCloudAndGSplat(), backendControlState),
  new MeasurePlugin(),
  new MeasureOverlayPlugin(),
  new ClipPlugin(),
  new ClipOverlayPlugin(),
]

const {
  engine,
  isLoading,
  modelInput,
  stats,
  loadedHandles,
  init,
  loadModel,
  removeAllModels,
  autoLoad,
} = use3DGS(containerRef, {
  plugins,
  defaultModelUrl: DEFAULT_GS3D_MODEL,
  autoLoadFromUrl: true,
  graphics: getGsplatGraphicsConfig(backendControlState),
  fov: 65,
  background: 'black',
  renderMode: isGpuTimingRequested() ? 'continuous' : 'on-demand',
  loadSHN: loadSHN.value,
})

// stats.js 性能面板：通过 statsEnabled 响应式开关控制显示/隐藏
const statsEnabled = ref(false)
let statsInstance: Stats | null = null
let statsBeforeRenderOff: (() => void) | null = null
let statsAfterRenderOff: (() => void) | null = null

const cameraMode = ref<CameraMode>(CameraMode.Perspective)
const controlMode = ref<ControlMode>('orbit')
const spaceMouseNavigation = ref<SpaceMouseNavigationState>(
  createSpaceMouseNavigationState(SPACEMOUSE_DEFAULT_PROFILE),
)
const activeTool = ref<string | null>(null)
const measureType = ref<MeasureType>('distance')
const clipSelectionMode = ref<ClipSelectionMode>('polygon')
const clipApplyMode = ref<ClipApplyMode>('inside')
const clipCanConfirm = ref(false)
const measureCount = ref(0)
const clipCount = ref(0)
const clipLodDiagnostics = ref({
  classifiedNodes: 0,
  polygonCulledNodes: 0,
  boxCulledNodes: 0,
  mixedCulledNodes: 0,
  intersectingNodes: 0,
  excludedSplats: 0,
})
const loadError = ref('')
const cleanupTasks: Array<() => void> = []
let spaceMouseDefaultApplied = false

const activeToolLabel = computed(() => {
  if (activeTool.value === 'measure') {
    return '测量工具'
  }

  if (activeTool.value === 'clip') {
    return '裁切工具'
  }

  return '未启用'
})

const cameraModeLabel = computed(() => {
  return cameraMode.value === CameraMode.Perspective ? '透视' : '正交'
})

/** 标量过滤作用于最后加载的 3DGS 资产；卸载后自动回落到 null。 */
const activeScalarHandle = computed(() => loadedHandles.value.at(-1) ?? null)

const modelLoaderHint = computed(() => {
  if (loadError.value) {
    return `加载失败：${loadError.value}`
  }

  return '请选择包含 lod-meta.json 的本地目录，或直接选择单个 .ply 高斯文件，然后点击“加载模型”。'
})

const activeMeasureDetail = computed(() => {
  return MEASURE_OPTIONS.find((item) => item.value === measureType.value)
})

const activeClipSelectionDetail = computed(() => {
  return CLIP_SELECTION_OPTIONS.find((item) => item.value === clipSelectionMode.value)
})

const activeClipApplyDetail = computed(() => {
  return CLIP_APPLY_OPTIONS.find((item) => item.value === clipApplyMode.value)
})

const isLiveBoxClipMode = computed(() => clipSelectionMode.value === 'box3d')

const clipCardBadge = computed(() => {
  if (isLiveBoxClipMode.value) {
    return activeClipSelectionDetail.value?.label ?? '3D Box'
  }

  return `${activeClipSelectionDetail.value?.label ?? '多边形'} / ${activeClipApplyDetail.value?.label ?? '内裁'}`
})

const clipCardDescription = computed(() => {
  if (isLiveBoxClipMode.value) {
    return '3D Box 按模型包围盒初始化并实时内裁；拖动 gizmo 会立即更新 Shader 精裁，并在下一次 LOD 评估中合并执行八叉树粗裁。'
  }

  return '多边形与矩形确认后生效；叶节点先做八叉树保守粗裁，边界节点继续由 Shader 按中心点精裁。'
})

const statusItems = computed(() => {
  return [
    { label: '当前工具', value: activeToolLabel.value },
    { label: '相机模式', value: cameraModeLabel.value },
    {
      label: '控制器',
      value:
        CONTROL_MODE_OPTIONS.find((item) => item.value === controlMode.value)?.label ??
        controlMode.value,
    },
    { label: '已加载模型', value: stats.value.totalModels },
    { label: '测量结果', value: measureCount.value },
    { label: '裁切体', value: clipCount.value },
    {
      label: '粗裁节点（2D / 3D）',
      value: `${clipLodDiagnostics.value.polygonCulledNodes} / ${clipLodDiagnostics.value.boxCulledNodes}`,
    },
    { label: '边界精裁节点', value: clipLodDiagnostics.value.intersectingNodes },
    {
      label: '预算排除 splat',
      value: clipLodDiagnostics.value.excludedSplats.toLocaleString(),
    },
  ]
})

const measureNote = computed(() => {
  return `当前测量：${activeMeasureDetail.value?.label ?? '距离'}。移动鼠标会高亮候选点，右键结束当前测量。`
})

function getMeasureService(): MeasureService | null {
  const engineInstance = engine.value

  if (!engineInstance || !engineInstance.context.hasService('MeasureService')) {
    return null
  }

  return engineInstance.context.getService('MeasureService')
}

function getClipService(): ClipService | null {
  const engineInstance = engine.value

  if (!engineInstance || !engineInstance.context.hasService('ClipService')) {
    return null
  }

  return engineInstance.context.getService('ClipService')
}

function syncClipLodDiagnostics() {
  const engineInstance = engine.value
  if (!engineInstance?.context.hasService('GaussianSplatService')) {
    return
  }

  const diagnostics = engineInstance.context.getService('GaussianSplatService').getLodStats()
    .clipLodDiagnostics
  if (
    diagnostics &&
    (clipLodDiagnostics.value.classifiedNodes !== diagnostics.classifiedNodes ||
      clipLodDiagnostics.value.polygonCulledNodes !== diagnostics.polygonCulledNodes ||
      clipLodDiagnostics.value.boxCulledNodes !== diagnostics.boxCulledNodes ||
      clipLodDiagnostics.value.mixedCulledNodes !== diagnostics.mixedCulledNodes ||
      clipLodDiagnostics.value.intersectingNodes !== diagnostics.intersectingNodes ||
      clipLodDiagnostics.value.excludedSplats !== diagnostics.excludedSplats)
  ) {
    clipLodDiagnostics.value = { ...diagnostics }
  }
}

function getSpaceMouseController(): SpaceMouseControllerLike | null {
  const controller = engine.value?.controls.getController('spacemouse') as
    | SpaceMouseControllerLike
    | undefined

  return controller ?? null
}

function syncSpaceMouseNavigationState() {
  spaceMouseNavigation.value = readSpaceMouseNavigationState(
    getSpaceMouseController(),
    SPACEMOUSE_DEFAULT_PROFILE,
  )
}

function ensureSpaceMouseDefaultProfile() {
  if (spaceMouseDefaultApplied) {
    return
  }

  const controller = getSpaceMouseController()
  if (!controller) {
    return
  }

  applySpaceMouseNavigationProfile(controller, SPACEMOUSE_DEFAULT_PROFILE)
  spaceMouseDefaultApplied = true
  syncSpaceMouseNavigationState()
}

function syncPanelState() {
  const engineInstance = engine.value

  if (!engineInstance) {
    activeTool.value = null
    measureCount.value = 0
    clipCount.value = 0
    clipCanConfirm.value = false
    clipLodDiagnostics.value = {
      classifiedNodes: 0,
      polygonCulledNodes: 0,
      boxCulledNodes: 0,
      mixedCulledNodes: 0,
      intersectingNodes: 0,
      excludedSplats: 0,
    }
    spaceMouseNavigation.value = createSpaceMouseNavigationState(SPACEMOUSE_DEFAULT_PROFILE)
    return
  }

  activeTool.value = engineInstance.activeTool
  cameraMode.value = engineInstance.camera.mode
  controlMode.value = engineInstance.controls.activeMode
  syncSpaceMouseNavigationState()
  syncClipLodDiagnostics()

  const measureService = getMeasureService()
  if (measureService) {
    measureType.value = measureService.getMeasureType()
    measureCount.value = measureService.getResults().length
  } else {
    measureCount.value = 0
  }

  const clipService = getClipService()
  if (clipService) {
    clipSelectionMode.value = clipService.getSelectionMode() as ClipSelectionMode
    clipApplyMode.value = clipService.getClipApplyMode()
    clipCanConfirm.value = clipService.getDraftState().canConfirm
    clipCount.value = clipService.volumes.filter(
      (volume) => volume.clipTask !== ClipTask.HIGHLIGHT,
    ).length
  } else {
    clipCount.value = 0
    clipCanConfirm.value = false
  }
}

// 启用 stats.js：挂载 DOM 面板到容器左上角，并绑定 frame.begin/frame.end 更新
function enableStats() {
  const engineInstance = engine.value
  const container = containerRef.value
  if (!engineInstance || !container || statsInstance) { return }

  const instance = new Stats()
  instance.showPanel(0)
  const dom = instance.dom
  // 使用 fixed 相对视口定位，避免被父容器 overflow:hidden / stacking context 干扰；
  // z-index 需高于 SceneControlPanel(1000) 才能显示在右上角控制面板之上
  dom.style.position = 'fixed'
  dom.style.left = '16px'
  dom.style.top = '16px'
  dom.style.right = 'auto'
  dom.style.zIndex = '1100'
  document.body.appendChild(dom)
  statsInstance = instance

  const onBegin = () => { instance.begin() }
  const onEnd = () => { instance.end() }
  engineInstance.events.on('frame.begin', onBegin)
  engineInstance.events.on('frame.end', onEnd)
  statsBeforeRenderOff = () => engineInstance.events.off('frame.begin', onBegin)
  statsAfterRenderOff = () => engineInstance.events.off('frame.end', onEnd)
}

// 禁用 stats.js：解绑事件并从 DOM 移除面板
function disableStats() {
  statsBeforeRenderOff?.()
  statsAfterRenderOff?.()
  statsBeforeRenderOff = null
  statsAfterRenderOff = null
  if (statsInstance) {
    statsInstance.dom.remove()
    statsInstance = null
  }
}

function teardownBindings() {
  while (cleanupTasks.length > 0) {
    cleanupTasks.pop()?.()
  }
}

function bindPanelState() {
  const engineInstance = engine.value

  if (!engineInstance) {
    return
  }

  teardownBindings()
  ensureSpaceMouseDefaultProfile()

  const handleChange = () => {
    syncPanelState()
  }
  const handleFrameEnd = () => {
    syncClipLodDiagnostics()
  }

  engineInstance.events.on('tool.activated', handleChange)
  engineInstance.events.on('tool.deactivated', handleChange)
  engineInstance.events.on('tool.resultChanged', handleChange)
  engineInstance.events.on('camera.modeChanged', handleChange)
  engineInstance.events.on('controls.modeChanged', handleChange)
  engineInstance.events.on('controls.spacemouseNavigationChanged', handleChange)
  engineInstance.events.on('frame.end', handleFrameEnd)

  cleanupTasks.push(() => engineInstance.events.off('tool.activated', handleChange))
  cleanupTasks.push(() => engineInstance.events.off('tool.deactivated', handleChange))
  cleanupTasks.push(() => engineInstance.events.off('tool.resultChanged', handleChange))
  cleanupTasks.push(() => engineInstance.events.off('camera.modeChanged', handleChange))
  cleanupTasks.push(() => engineInstance.events.off('controls.modeChanged', handleChange))
  cleanupTasks.push(() =>
    engineInstance.events.off('controls.spacemouseNavigationChanged', handleChange),
  )
  cleanupTasks.push(() => engineInstance.events.off('frame.end', handleFrameEnd))

  const clipSubscription = getClipService()?.onChanged(handleChange)
  if (clipSubscription) {
    cleanupTasks.push(() => clipSubscription.dispose())
  }

  syncPanelState()
}

async function handleLoadModel() {
  loadError.value = ''

  try {
    if (stats.value.totalModels > 0) {
      clearSceneModels()
    }

    await loadModel(undefined, { loadSHN: loadSHN.value })
    syncPanelState()
  } catch (error) {
    loadError.value = error instanceof Error ? error.message : '未知错误'
  }
}

function clearSceneModels() {
  const plugin = engine.value?.plugins.get<GaussianSplatPlugin>('gaussian-splat')

  for (const handle of plugin?.getAll() ?? []) {
    plugin?.unmount(handle.id)
    plugin?.dispose(handle.id)
  }

  removeAllModels(false)
  loadError.value = ''
  syncPanelState()
}

function activateMeasureTool(type: MeasureType) {
  const measureService = getMeasureService()

  measureService?.setMeasureType(type)
  engine.value?.activateTool('measure')
  syncPanelState()
}

function handleMeasureSelection(value: string) {
  activateMeasureTool(value as MeasureType)
}

function clearMeasureResults() {
  getMeasureService()?.clearResults()
  syncPanelState()
}

function activateClipTool(mode: ClipSelectionMode) {
  getClipService()?.setSelectionMode(mode)
  engine.value?.activateTool('clip')
  syncPanelState()
}

function handleClipSelection(value: string) {
  activateClipTool(value as ClipSelectionMode)
}

function setClipApplyMode(mode: ClipApplyMode) {
  getClipService()?.setClipApplyMode(mode)
  engine.value?.activateTool('clip')
  syncPanelState()
}

function handleClipApplySelection(value: string) {
  setClipApplyMode(value as ClipApplyMode)
}

function confirmClipDraft() {
  getClipService()?.confirmDraft()
  syncPanelState()
}

function clearClipVolumes() {
  getClipService()?.clearVolumes()
  syncPanelState()
}

function deactivateTool() {
  engine.value?.deactivateTool()
  syncPanelState()
}

function requiresPerspectiveControlMode(mode: ControlMode): boolean {
  return mode === 'first-person' || mode === 'legacy-first-person' || mode === 'fly'
}

function toggleCameraMode() {
  const engineInstance = engine.value

  if (!engineInstance) {
    return
  }

  const nextMode =
    cameraMode.value === CameraMode.Perspective
      ? CameraMode.Orthographic
      : CameraMode.Perspective

  if (nextMode === CameraMode.Orthographic && requiresPerspectiveControlMode(controlMode.value)) {
    engineInstance.controls.setMode('orbit')
  }

  engineInstance.camera.setMode(nextMode)
  syncPanelState()
}

function setView(preset: ViewPreset) {
  engine.value?.camera.setPresetView(preset)
}

function handleViewSelection(value: string) {
  setView(value as ViewPreset)
}

function fitAll() {
  engine.value?.fitAll()
}

function setControlMode(mode: ControlMode) {
  if (!engine.value) {
    return
  }

  if (requiresPerspectiveControlMode(mode) && engine.value.camera.mode !== CameraMode.Perspective) {
    engine.value.camera.setMode(CameraMode.Perspective)
  }

  engine.value.controls.setMode(mode)
  syncPanelState()
}

function handleControlModeSelection(value: string) {
  setControlMode(value as ControlMode)
}

function handleSpaceMouseSecondaryControlGroupSelection(payload: {
  groupId: string
  value: string
}) {
  applySpaceMouseSecondaryControlSelection(
    getSpaceMouseController(),
    payload.groupId,
    payload.value,
  )
  syncSpaceMouseNavigationState()
}

// 监听 statsEnabled 切换，动态启用/禁用 stats 面板
watch(statsEnabled, (next) => {
  if (next) {
    enableStats()
  } else {
    disableStats()
  }
})

onMounted(async () => {
  graphicsCard.markCreating()
  try {
    await init()
  } catch (error) {
    graphicsCard.markError(toGraphicsRuntimeError(error))
    return
  }
  graphicsCard.markReady(engine.value ? engine.value.getGraphicsInfo().kind : null)
  bindPanelState()
  if (!modelInput.value) {
    modelInput.value = DEFAULT_GS3D_MODEL
  }
  await autoLoad()
  syncPanelState()
  // 引擎初始化后若开关已开启，则立即挂载 stats 面板
  if (statsEnabled.value) {
    enableStats()
  }
})

onBeforeUnmount(() => {
  disableStats()
  teardownBindings()
})
</script>

<template>
  <div class="gs3d-page">
    <div class="render-container" ref="containerRef"></div>

    <button class="stats-toggle" :class="{ active: statsEnabled }" type="button" @click="statsEnabled = !statsEnabled">
      {{ statsEnabled ? '隐藏 FPS' : '显示 FPS' }}
    </button>

    <SceneControlPanel
      layout="sidebar"
      kicker="右侧面板"
      title="控制中心"
      :meta="`${activeToolLabel} / ${cameraModeLabel}`"
    >
      <SceneAssetLoaderCard v-model="modelInput" badge="高斯场景" :hint="modelLoaderHint" :loading="isLoading"
        :can-clear="stats.totalModels > 0" :directory-entry-names="['lod-meta.json']"
        :allow-file="true" file-accept=".ply" :file-entry-extensions="['ply']" file-pick-label="选择 PLY 文件"
        :preset-models="PRESET_MODELS" @submit-load="handleLoadModel()" @clear-scene="clearSceneModels()">
        <template #options>
          <button class="load-option-toggle" :class="{ active: loadSHN }" type="button" role="switch"
            :aria-checked="loadSHN" :disabled="isLoading" @click.stop="loadSHN = !loadSHN">
            <span>
              <strong>高阶球谐（SHN）</strong>
              <small>关闭时跳过 SHN，仍保留 SH0 基础颜色</small>
            </span>
            <span class="load-option-state">{{ loadSHN ? '加载' : '跳过' }}</span>
          </button>
        </template>
      </SceneAssetLoaderCard>

      <GraphicsBackendControlCard :card="graphicsCard">
        <template #extensions>
          <GSplatBackendControlCard :state="backendControlState" />
        </template>
      </GraphicsBackendControlCard>

      <SceneStatusCard badge="高斯场景" :items="statusItems" hint="3DGS 裁切统一使用八叉树叶节点粗裁 + Shader 中心点精裁；当前适用于 unified LOD SOG。"
        action-label="停用当前工具" action-variant="subtle" :action-disabled="!activeTool" @action="deactivateTool()" />

      <GSplatScalarFilterCard :handle="activeScalarHandle" :disabled="isLoading" />

      <SceneGroupCard title="工具类" subtitle="测量与裁切都收纳在这里" :badge="String(measureCount + clipCount)">
        <SceneMeasureCard :options="MEASURE_OPTIONS" :active-label="activeMeasureDetail?.label ?? '距离'"
          :active-value="measureType" :active="activeTool === 'measure'" :clear-disabled="measureCount === 0"
          :note="measureNote" @select="handleMeasureSelection" @clear="clearMeasureResults()" />

        <SceneClipCard
          :badge="clipCardBadge"
          :description="clipCardDescription"
          :selection-options="CLIP_SELECTION_OPTIONS"
          :selection-value="clipSelectionMode"
          :apply-options="CLIP_APPLY_OPTIONS"
          :apply-value="clipApplyMode"
          :active="activeTool === 'clip'"
          :live-box="isLiveBoxClipMode"
          :can-confirm="clipCanConfirm"
          :clear-disabled="clipCount === 0"
          @select-selection="handleClipSelection"
          @select-apply="handleClipApplySelection"
          @confirm="confirmClipDraft()"
          @clear="clearClipVolumes()"
        />
      </SceneGroupCard>

      <SceneViewCard :badge="cameraModeLabel" :is-perspective="cameraMode === CameraMode.Perspective"
        :options="VIEW_OPTIONS" :control-mode-options="CONTROL_MODE_OPTIONS" :active-control-mode="controlMode"
        :secondary-control-groups="
          controlMode === 'spacemouse' ? buildSpaceMouseSecondaryControlGroups(spaceMouseNavigation) : []
        "
        @toggle-mode="toggleCameraMode()" @fit="fitAll()" @set-view="handleViewSelection"
        @set-control-mode="handleControlModeSelection"
        @set-secondary-control-group-mode="handleSpaceMouseSecondaryControlGroupSelection" />
    </SceneControlPanel>
  </div>
</template>

<style scoped>
.gs3d-page {
  --panel-gap: 0px;
  width: 100%;
  height: 100vh;
  position: relative;
  overflow: hidden;
  display: grid;
  grid-template-columns: minmax(0, 7fr) minmax(0, 3fr);
  gap: var(--panel-gap);
  padding: var(--panel-gap);
  box-sizing: border-box;
  align-items: stretch;
}

.load-option-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 12px 14px;
  border: 1px solid rgba(150, 170, 255, 0.16);
  border-radius: 16px;
  color: rgba(238, 244, 255, 0.86);
  background: linear-gradient(180deg, rgba(11, 16, 28, 0.96), rgba(18, 25, 40, 0.88));
  cursor: pointer;
}

.load-option-toggle > span:first-child {
  display: grid;
  gap: 4px;
  text-align: left;
}

.load-option-toggle strong {
  font-size: 12px;
}

.load-option-toggle small {
  color: rgba(184, 201, 255, 0.68);
}

.load-option-toggle:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.load-option-toggle.active {
  border-color: rgba(100, 216, 255, 0.55);
  background: linear-gradient(180deg, rgba(18, 55, 77, 0.96), rgba(13, 36, 55, 0.9));
}

.load-option-state {
  flex: 0 0 auto;
  margin-left: 12px;
  padding: 4px 9px;
  border-radius: 999px;
  color: #dfe9ff;
  background: rgba(113, 132, 214, 0.26);
  font-size: 11px;
}

.load-option-toggle.active .load-option-state {
  color: #bff4ff;
  background: rgba(51, 188, 222, 0.24);
}

.render-container {
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  position: relative;
}

.stats-toggle {
  position: absolute;
  left: 32px;
  bottom: 32px;
  z-index: 1100;
  padding: 8px 14px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  font-size: 13px;
  cursor: pointer;
  backdrop-filter: blur(4px);
  pointer-events: auto;
}

.stats-toggle.active {
  background: rgba(64, 160, 255, 0.85);
  border-color: rgba(255, 255, 255, 0.6);
}
</style>
